import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, AlertTriangle, Check, Loader2 } from '../../icons/wp-icons';
import { resetOnboarding } from '../../api/onboardingApi';

function OnboardingResetSettings({ readOnly = false }) {
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async () => {
    setResetting(true);
    setError('');

    try {
      await resetOnboarding();
      // Nach Reset zum Onboarding navigieren
      navigate('/onboarding');
    } catch (err) {
      console.error('Reset error:', err);
      setError('Fehler beim Zurücksetzen: ' + (err.response?.data?.error || err.message));
      setResetting(false);
    }
  };

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg shrink-0">
            <RotateCcw className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold">Onboarding neu starten</h2>
            <p className="text-xs sm:text-sm text-text-muted hidden sm:block">
              Starten Sie den Einrichtungsassistenten erneut
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            Wenn Sie den Onboarding-Prozess neu starten, werden alle bisherigen Einstellungen
            (Branche, Öffnungszeiten, KI-Agent-Konfiguration) zurückgesetzt. Firmendaten, Kunden,
            Rechnungen und andere Daten bleiben erhalten.
          </p>

          {readOnly ? (
            <p className="text-sm text-text-muted italic">
              Sie haben keine Berechtigung, diese Einstellung zu ändern.
            </p>
          ) : !showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              className="flex items-center gap-2 px-4 py-3 bg-orange-100 text-orange-700 rounded-button hover:bg-orange-200 transition font-medium"
            >
              <RotateCcw className="w-4 h-4" />
              Onboarding neu starten
            </button>
          ) : (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-900">
                    Sind Sie sicher?
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    Folgende Einstellungen werden zurückgesetzt:
                  </p>
                  <ul className="text-sm text-orange-700 mt-2 list-disc ml-4">
                    <li>Branchenauswahl</li>
                    <li>Öffnungszeiten</li>
                    <li>KI-Agent System-Prompt</li>
                    <li>Nicht angebotene Leistungen</li>
                    <li>Pflichtfelder-Konfiguration</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition font-medium text-sm"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-warning text-white rounded-button hover:bg-amber-700 transition font-medium text-sm disabled:opacity-50"
                >
                  {resetting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wird zurückgesetzt...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Ja, neu starten
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingResetSettings;
